/**
 * ExecutionCockpit — the third surface in the Build / Traction / Execution
 * trilogy. Same UX grammar as the Build & Traction cockpits:
 *
 *   • LEFT  (preview)  → the venture's execution state, made visible:
 *                         live Activity Feed of agent moves, plus three
 *                         contextual spaces (Contacts · Content · Placement)
 *                         that surface actionable items pulled from the
 *                         Traction library (personas, competitors, BM).
 *   • RIGHT (chat)     → Aria, the orchestrator. The founder asks her to
 *                         do things; she delegates to the project agents.
 *                         Replies are context-aware (see _aria_cockpit_reply
 *                         on the backend).
 *
 * The component is opened from the HQ view via a project selector. The
 * selector only lists ventures that have a generated traction layer
 * (dataroom + personas + business models present).
 */
import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import {
  Activity, Mail, Phone, Megaphone, Palette, Send, ExternalLink,
  Loader2, Sparkles, ArrowRight,
  Users, MessageCircle, Target, Briefcase, Award, Globe2,
} from 'lucide-react';
import { Linkedin, Instagram, Twitter, Youtube } from '../icons/BrandIcons';
import CockpitChat from './CockpitChat';
import BuildImprovements from './BuildImprovements';
import DailyBriefsPanel from '../execution/DailyBriefsPanel';
import LoopPanel from '../execution/LoopPanel';
import ExecutionChat from '../execution/ExecutionChat';
import { paletteForVenture, DEFAULT_PALETTE } from './brandInheritance';

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function ExecutionCockpit({ monetizeId, onBack, onOpenInWorkshop }) {
  const [venture, setVenture] = useState(null);
  const [messages, setMessages] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loopId, setLoopId] = useState(null);
  const chatRef = useRef(null);

  const palette = useMemo(() => paletteForVenture(venture || { name: '' }), [venture]);

  const load = useCallback(async () => {
    if (!monetizeId) return;
    try {
      const [v, h] = await Promise.all([
        fetch(`${API}/api/monetize/projects/${monetizeId}`, { credentials: 'include' }).then(r => r.ok ? r.json() : null),
        fetch(`${API}/api/hq/${monetizeId}`, { credentials: 'include' }).then(r => r.ok ? r.json() : null),
      ]);
      if (v) setVenture(v);
      if (h?.messages) setMessages(h.messages);
      if (h?.agents) setAgents(h.agents);
    } catch {
      // Polling silently retries on the next tick.
    }
  }, [monetizeId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [load]);

  if (!venture) {
    return (
      <div style={{
        flex: 1, height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: DEFAULT_PALETTE.bg, color: DEFAULT_PALETTE.textMute, fontSize: 12,
      }}>
        <Loader2 size={14} style={{ animation: 'spin 0.7s linear infinite', marginRight: 8 }} />
        Loading execution cockpit…
      </div>
    );
  }

  return (
    <div
      data-testid="execution-cockpit"
      data-palette={palette.key}
      style={{ flex: 1, height: '100%', display: 'flex', minHeight: 0, overflow: 'hidden' }}
    >
      {/* LEFT — Execution Plan (scrollable) */}
      <ExecutionPreview
        venture={venture}
        messages={messages}
        agents={agents}
        palette={palette}
        onBack={onBack}
        onSelectQuestion={(q) => chatRef.current?.openQuestion(q)}
        onSelectTask={(t) => chatRef.current?.openTask(t)}
        onLoopLoaded={(l) => setLoopId(l?.loop_id || null)}
        onOpenInWorkshop={onOpenInWorkshop}
      />
      {/* RIGHT — Persistent unified chat (Aria · Lyra · Hermes) */}
      <ExecutionChat
        ref={chatRef}
        projectId={venture.monetize_id}
        loopId={loopId}
        onAnswerSubmitted={load}
      />
    </div>
  );
}

/* ───────────────── EXECUTION PREVIEW (left pane) ───────────────── */

function ExecutionPreview({ venture, messages, agents, palette, onBack, onSelectQuestion, onSelectTask, onLoopLoaded, onOpenInWorkshop }) {
  const outputs = venture.outputs || {};
  const personas = outputs.personas?.data?.personas || [];
  const competitors = outputs.dataroom?.data?.competitor_mapping || [];
  const investors = outputs.investors?.data?.investors || [];
  const networkInf = outputs.network?.data?.connections || [];
  const recommendedBM = (() => {
    const bm = outputs.business_models?.data;
    if (!bm?.models?.length) return null;
    return bm.models.find(m => m.id === bm.recommended_id) || bm.models[0];
  })();

  return (
    <div style={{
      flex: 1, height: '100%', overflowY: 'auto', minWidth: 0,
      background: palette.bg, color: palette.text,
      position: 'relative',
    }}>
      <div style={{
        position: 'absolute', inset: 0, pointerEvents: 'none',
        background: `radial-gradient(60% 50% at 50% 0%, ${palette.accent}10, transparent 70%)`,
      }} />
      <div style={{ position: 'relative', padding: '24px 28px 40px' }}>
        <ExecHeader venture={venture} palette={palette} agents={agents} onBack={onBack} />
        {/* THE LOOP — left-side execution plan; the persistent chat lives on
            the right of the cockpit and picks up question/task selection. */}
        <LoopPanel
          projectId={venture.monetize_id}
          onSelectQuestion={onSelectQuestion}
          onSelectTask={onSelectTask}
          onLoopLoaded={onLoopLoaded}
          onOpenInWorkshop={onOpenInWorkshop}
        />
      </div>
    </div>
  );
}

function ExecHeader({ venture, palette, agents, onBack }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      marginBottom: 20, gap: 14,
    }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: 9, fontWeight: 700, letterSpacing: '0.22em', textTransform: 'uppercase',
          color: palette.textDim, display: 'inline-flex', alignItems: 'center', gap: 6,
        }}>
          <Activity size={11} color={palette.accent} /> Execution Cockpit
        </div>
        <h2 style={{
          fontSize: 22, fontWeight: 800, color: palette.text, marginTop: 4, marginBottom: 0,
          letterSpacing: '-0.015em',
        }}>
          {venture?.name}
        </h2>
        <div style={{ fontSize: 11, color: palette.textMute, marginTop: 4 }}>
          {agents.length} project agent{agents.length !== 1 ? 's' : ''} active · Aria coordinates
        </div>
      </div>
      {onBack && (
        <button
          onClick={onBack}
          data-testid="exec-back-btn"
          style={{
            padding: '8px 13px', fontSize: 10, fontWeight: 700,
            background: 'transparent', color: palette.textMute,
            border: `1px solid ${palette.border}`,
            cursor: 'pointer', fontFamily: 'inherit',
            letterSpacing: '0.06em', textTransform: 'uppercase',
            borderRadius: 6,
          }}
        >
          ← Back to office
        </button>
      )}
    </div>
  );
}

/* ─────────── Activity Feed (intelligent narration of agent moves) ─────────── */

function ActivityFeed({ messages, palette }) {
  // Surface the most recent 10 agent actions. Plain user notes are filtered out.
  const items = useMemo(() => {
    return (messages || [])
      .filter(m => m.from_kind !== 'user')
      .slice(-20)
      .reverse();
  }, [messages]);

  return (
    <section
      data-testid="activity-feed"
      style={{
        marginBottom: 22,
        padding: 16,
        background: palette.surface,
        border: `1px solid ${palette.border}`,
        borderRadius: 14,
      }}
    >
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12,
      }}>
        <span style={{
          width: 6, height: 6, borderRadius: 999,
          background: palette.accent, boxShadow: `0 0 6px ${palette.accent}`,
          animation: 'pulse-glow 1.6s ease-in-out infinite',
        }} />
        <div style={{
          fontSize: 10, fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase',
          color: palette.textDim,
        }}>
          Activity Feed · agent moves
        </div>
      </div>
      {items.length === 0 ? (
        <div style={{ fontSize: 11.5, color: palette.textMute, padding: 10, fontStyle: 'italic' }}>
          No moves yet. Ask Aria to delegate the first task in the chat.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {items.slice(0, 8).map((m, i) => (
            <ActivityRow key={m.message_id || i} m={m} palette={palette} />
          ))}
        </div>
      )}
    </section>
  );
}

function ActivityRow({ m, palette }) {
  const author = m.from_agent_id === 'hermes' ? 'Aria' : (m.from_name || 'Aria');
  const color = (m.from_agent_id === 'hermes' || author === 'Aria') ? palette.accent : (m.from_color || palette.accent2);
  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: 10,
      padding: '8px 10px',
      background: palette.surface2,
      border: `1px solid ${palette.border}`,
      borderRadius: 8,
    }}>
      <div style={{
        width: 22, height: 22, flexShrink: 0,
        background: `${color}22`, color,
        border: `1px solid ${color}55`,
        borderRadius: '50%',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 10, fontWeight: 800,
      }}>{author[0].toUpperCase()}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 11.5, fontWeight: 700, color: palette.text }}>{author}</span>
          {m.kind && m.kind !== 'note' && (
            <span style={{
              fontSize: 8, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase',
              padding: '1px 6px',
              background: `${color}1A`, color,
              border: `1px solid ${color}33`,
              borderRadius: 999,
            }}>{m.kind.replace(/[-_]/g, ' ')}</span>
          )}
        </div>
        <div style={{ fontSize: 11, color: palette.textMute, marginTop: 3, lineHeight: 1.5 }}>
          {(m.message || '').slice(0, 200)}
        </div>
      </div>
    </div>
  );
}

/* ─────────── 3 Execution Spaces (Contacts · Content · Placement) ─────────── */

function ExecSpaces({ venture, palette, personas, competitors, investors, networkInf, recommendedBM, agents }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <SpaceCard
        testid="space-contacts"
        title="Contacts"
        subtitle="Email · Calls · Outreach"
        Icon={Mail}
        accent={palette.accent}
        palette={palette}
        agents={agents.filter(a => /investor|capital|mentor|persona|outreach|sales/i.test((a.role || '') + (a.kind || '') + (a.name || '')))}
        actions={buildContactActions(personas, investors, palette)}
      />
      <SpaceCard
        testid="space-content"
        title="Content"
        subtitle="Design · Ads · Communication"
        Icon={Palette}
        accent={palette.accent2 || palette.accent}
        palette={palette}
        agents={agents.filter(a => /content|gtm|growth|builder|designer/i.test((a.role || '') + (a.kind || '') + (a.name || '')))}
        actions={buildContentActions(recommendedBM, competitors, palette)}
      />
      <SpaceCard
        testid="space-placement"
        title="Placement"
        subtitle="Creator partnerships · Product placement"
        Icon={Megaphone}
        accent={'#F472B6'}
        palette={palette}
        agents={agents.filter(a => /grants|gtm|content|growth/i.test((a.role || '') + (a.kind || '') + (a.name || '')))}
        actions={buildPlacementActions(networkInf, competitors, personas, palette)}
        flagship
      />
    </div>
  );
}

function SpaceCard({ testid, title, subtitle, Icon, accent, palette, agents, actions, flagship }) {
  return (
    <section
      data-testid={testid}
      style={{
        padding: 16,
        background: palette.surface,
        border: `1px solid ${flagship ? accent + '55' : palette.border}`,
        borderRadius: 14,
        position: 'relative', overflow: 'hidden',
      }}
    >
      {flagship && (
        <div style={{
          position: 'absolute', top: 0, right: 0,
          padding: '3px 10px', fontSize: 8, fontWeight: 800, letterSpacing: '0.14em',
          background: accent, color: palette.bg,
          borderBottomLeftRadius: 8,
        }}>
          FOUNDER EDGE
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
        <div style={{
          width: 32, height: 32, flexShrink: 0,
          background: `${accent}1F`, color: accent,
          border: `1px solid ${accent}55`,
          borderRadius: 9,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Icon size={15} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: palette.text, letterSpacing: '-0.01em' }}>{title}</div>
          <div style={{ fontSize: 10.5, color: palette.textDim, letterSpacing: '0.04em' }}>{subtitle}</div>
        </div>
        {agents.length > 0 && (
          <div style={{
            display: 'flex', gap: -4, marginLeft: 'auto',
          }}>
            {agents.slice(0, 4).map((a, i) => (
              <div key={a.agent_id || i} title={`${a.name} · ${a.role}`} style={{
                width: 24, height: 24,
                background: `${a.color || palette.accent}22`, color: a.color || palette.accent,
                border: `1px solid ${a.color || palette.accent}55`,
                borderRadius: '50%',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 9, fontWeight: 800,
                marginLeft: i === 0 ? 0 : -6,
                zIndex: 4 - i,
              }}>
                {a.avatar || (a.name || '?')[0].toUpperCase()}
              </div>
            ))}
            {agents.length > 4 && (
              <div style={{
                width: 24, height: 24,
                background: palette.surface2, color: palette.textMute,
                border: `1px solid ${palette.border}`,
                borderRadius: '50%',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 9, fontWeight: 800, marginLeft: -6,
              }}>+{agents.length - 4}</div>
            )}
          </div>
        )}
      </div>

      {actions.length === 0 ? (
        <div style={{ fontSize: 11, color: palette.textMute, padding: 8, fontStyle: 'italic' }}>
          No actionable items yet — Aria will surface them as the docs grow.
        </div>
      ) : (
        <div style={{
          display: 'grid', gap: 8,
          gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
        }}>
          {actions.map((act, i) => (
            <ActionItem key={i} act={act} palette={palette} accent={accent} />
          ))}
        </div>
      )}
    </section>
  );
}

function ActionItem({ act, palette, accent }) {
  const Icon = act.Icon || ArrowRight;
  return (
    <div
      data-testid={`action-item`}
      style={{
        padding: 12,
        background: palette.surface2,
        border: `1px solid ${palette.border}`,
        borderRadius: 10,
        display: 'flex', flexDirection: 'column', gap: 6,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
        <Icon size={12} color={accent} />
        <div style={{ fontSize: 11.5, fontWeight: 700, color: palette.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {act.title}
        </div>
      </div>
      {act.subtitle && (
        <div style={{ fontSize: 10.5, color: palette.textMute, lineHeight: 1.5 }}>
          {act.subtitle}
        </div>
      )}
      <div style={{ display: 'flex', gap: 5, marginTop: 2, flexWrap: 'wrap' }}>
        {act.url && (
          <a
            href={act.url} target="_blank" rel="noopener noreferrer"
            style={{
              padding: '3px 8px', fontSize: 9, fontWeight: 700, letterSpacing: '0.05em',
              background: 'transparent', color: palette.textMute,
              border: `1px solid ${palette.border}`,
              borderRadius: 999, textDecoration: 'none',
              display: 'inline-flex', alignItems: 'center', gap: 4,
            }}
          >
            <ExternalLink size={9} /> Open
          </a>
        )}
        {act.cta && (
          <span style={{
            padding: '3px 8px', fontSize: 9, fontWeight: 800, letterSpacing: '0.05em',
            background: `${accent}1A`, color: accent,
            border: `1px solid ${accent}44`,
            borderRadius: 999,
          }}>
            {act.cta}
          </span>
        )}
      </div>
    </div>
  );
}

/* ───────────── Action builders from traction library data ───────────── */

function buildContactActions(personas, investors, palette) {
  const out = [];
  personas.slice(0, 3).forEach(p => {
    out.push({
      title: `Email ${p.name || 'persona'}`,
      subtitle: `${p.role || ''}${p.willingness_to_pay ? ' · WTP ' + p.willingness_to_pay : ''}`,
      Icon: Mail,
      cta: 'Ask Aria to draft',
    });
  });
  investors.slice(0, 2).forEach(inv => {
    out.push({
      title: `Call ${inv.name}`,
      subtitle: `${inv.stage || ''}${inv.ticket_min ? ' · ' + inv.ticket_min + '–' + (inv.ticket_max || '') : ''}`,
      Icon: Phone,
      cta: 'Brief Capital scout',
    });
  });
  return out;
}

function buildContentActions(recommendedBM, competitors, palette) {
  const out = [];
  if (recommendedBM?.name) {
    out.push({
      title: `Landing page for ${recommendedBM.name}`,
      subtitle: recommendedBM.first_dollar_path?.slice(0, 80) || 'Adapted to the recommended model',
      Icon: Palette,
      cta: 'Brief content team',
    });
  }
  competitors.slice(0, 2).forEach(c => {
    out.push({
      title: `Counter-positioning vs ${c.name}`,
      subtitle: c.weaknesses ? `Exploit: ${c.weaknesses.slice(0, 70)}` : c.positioning,
      Icon: Megaphone,
      url: c.url,
      cta: 'Draft an ad angle',
    });
  });
  return out;
}

function buildPlacementActions(networkInf, competitors, personas, palette) {
  const out = [];
  networkInf.slice(0, 4).forEach(inf => {
    const Platform = platformIconFor(inf.platform);
    out.push({
      title: inf.handle || inf.profile_url || 'Creator',
      subtitle: `${inf.platform || ''}${inf.followers ? ' · ' + inf.followers + ' followers' : ''}`,
      Icon: Platform,
      url: inf.profile_url,
      cta: 'Brief placement deal',
    });
  });
  // Synthesize 1-2 placement themes from competitors if no creator yet
  if (out.length < 2) {
    competitors.slice(0, 2).forEach(c => {
      out.push({
        title: `Placement angle vs ${c.name}`,
        subtitle: 'Find creators their audience trusts and offer a sponsored slot',
        Icon: Megaphone,
        cta: 'Ask Aria to map creators',
      });
    });
  }
  // And one anchored on the strongest persona
  if (personas[0]) {
    out.push({
      title: `Creators reaching "${personas[0].name}"`,
      subtitle: 'Top 5 micro-influencers with ICP overlap',
      Icon: Users,
      cta: 'Ask Aria to surface 5',
    });
  }
  return out;
}

function platformIconFor(p) {
  const lc = (p || '').toLowerCase();
  if (lc.includes('linkedin')) return Linkedin;
  if (lc.includes('insta'))    return Instagram;
  if (lc.includes('twitter') || lc === 'x') return Twitter;
  if (lc.includes('youtube'))  return Youtube;
  if (lc.includes('tiktok'))   return Megaphone;
  return Globe2;
}
